// File: src/components/DbtRulesManagement.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { notify } from '../../services/notification';

import "./dbtRulesManagement.scss";
import DBTTable from "./DBTTable";
import { constant } from "../../constants/constant";
import Header from "../../component/loaderComponent/Header/Header";
import { getDbtRulesListAction, DbtIsActiveAction } from "../../actions/dbtAction";

const data = [
  {
    id: 'DBT-2024-001',
    name: 'Q4 Electronics Promotion',
    description: 'Special discount for electronic components in western regions',
    period: 'Oct 15, 2024 - Dec 31, 2024',
    createdBy: 'Michael Chen'
  },
  {
    id: 'DBT-2024-002',
    name: 'Automotive Parts Incentive',
    description: 'Enhanced DBT for automotive cluster parts across multiple states',
    period: 'Nov 01, 2024 - Jan 31, 2025',
    createdBy: 'Sarah Johnson'
  },
  {
    id: 'DBT-2024-003',
    name: 'Regional Hardware Boost',
    description: 'Targeted DBT for hardware components in eastern zone',
    period: 'Dec 01, 2024 - Feb 28, 2025',
    createdBy: 'Robert Kumar'
  },
  {
    id: 'DBT-2024-004',
    name: 'PAN India Special Offer',
    description: 'Nationwide DBT for selected premium product categories',
    period: 'Jan 15, 2025 - Mar 31, 2025',
    createdBy: 'Lisa Patel'
  },
  {
    id: 'DBT-2024-005',
    name: 'Metro Cities Initiative',
    description: 'Enhanced DBT for metro pincode areas with high volume parts',
    period: 'Feb 01, 2025 - Apr 30, 2025',
    createdBy: 'David Wilson'
  }
];

export default function DbtRulesManagement() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const pageSize = constant?.paginationCount || 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [page, setPage] = useState(1);
  const [dbtRuleList, setDbtRuleList] = useState(data);
  const [loading, setLoading] = useState(false);
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((totalCount || 0) / pageSize)),
    [totalCount, pageSize]
  );

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, 500);

    return () => clearTimeout(timer);
  }, [query]);

  // Smart search type detection: numeric -> 'id', text/alphanumeric -> 'name'
  const searchType = useMemo(() => {
    if (!debouncedQuery.trim()) {
      return { key: null, value: "" };
    }

    const isNumericOnly = /^\d+$/.test(debouncedQuery.trim());

    return {
      key: isNumericOnly ? "id" : "name",
      value: debouncedQuery.trim()
    };
  }, [debouncedQuery]);

  // Reset to page 1 when search changes
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [debouncedQuery]);

  // Removed local filtering since API handles it
  // const filtered = useMemo(() => {
  //   if (!query.trim()) return dbtRuleList;
  //   const q = query.toLowerCase();
  //   return dbtRuleList.filter(
  //     (r) => r.id.toLowerCase().includes(q) || r.name.toLowerCase().includes(q)
  //   );
  // }, [query, dbtRuleList]);

  const go = (p) => setCurrentPage(Math.min(Math.max(p, 1), totalPages));

  const CreateDBTRule = (evt) => {
    navigate('/createdbt')
  }
  const handleViewClick = (viewId) => {
    navigate(`/view/${viewId}`);
  }
  const handleEditClick = (editId) => {
    navigate(`/edit/${editId}`);
  }
  const handleOnActiveClick = async (id, data) => {
  const apiReq = {
    status: data ? 'active' : 'inactive'
  };
    setLoading(true);
    let response = await dispatch(DbtIsActiveAction(apiReq, id));
    if (response?.status === 200) {
      notify.success(response.data.msg)
      fetchDBTRulesList();
      setLoading(false);
      window.scroll(0, 0);
    } else if (response === undefined) {
      setLoading(false);
    }
  }

  const handleCheckboxChange = (id, checked) => {
    if (checked) {
      handleOnActiveClick(id, "true");
    } else {
      handleOnActiveClick(id, "false");
    }
  };
  
  const fetchDBTRulesList = async () => {
    try {
      setLoading(true);
      let queryParam = "";
      if (searchType.key && searchType.value) {
        queryParam += `&${searchType.key}=${searchType.value}`;
      }

      const res = await dispatch(
        getDbtRulesListAction(currentPage, pageSize, queryParam)
      );

      if (res?.status === 200) {
        const apiData = res?.data?.results || [];

        // Map API response to table format
        const mappedData = apiData.map(item => {
          // Format the date period
          const periodStart = item.effective_from ? new Date(item.effective_from).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
          const periodEnd = item.effective_to ? new Date(item.effective_to).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
          const period = periodStart && periodEnd ? `${periodStart} - ${periodEnd}` : 'N/A';

          return {
            id: item.id || '',
            name: item.name || '',
            description: item.description || '',
            period: period,
            createdBy: item.created_by_admin_user_id ? `Admin User ${item.created_by_admin_user_id}` : 'N/A',
            mapping_criteria: item.mapping_criteria || '',
            effective_from: item.effective_from || '',
            effective_to: item.effective_to || ''
          };
        });

        setDbtRuleList(Array.isArray(mappedData) ? mappedData : []);
        const count = res?.data?.count || 0;
        setTotalCount(count);

        // clamp current page if it went out of range
        const lastPage = Math.max(1, Math.ceil(count / pageSize));
        if (currentPage > lastPage) setCurrentPage(lastPage);
      }
    } catch (error) {
      notify.error("Failed to load DBT Rules");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchDBTRulesList();
  }, [currentPage, debouncedQuery]);


  return (
    <div className="dbt-wrapper">
      <header className="dbt-header">
        <Header title={"DBT Rules Management"}/>
        {/* <h1 className="dbt-title">DBT Rules Management</h1> */}
        <p className="dbt-subtitle">
          Manage and configure DBT rules for different regions and parts
        </p>
      </header>

      <section className="dbt-search-card">
        <div className="dbt-searchbar">
          <div className="dbt-input-wrapper">
            <span className="icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="7" />
                <path d="M21 21l-4.35-4.35" />
              </svg>
            </span>
            <input
              type="text"
              placeholder="Search by DBT ID or Name"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <button className="dbt-create-btn"
            onClick={CreateDBTRule}
          >
            <span className="plus">+</span> Create New DBT Rule
          </button>
        </div>
      </section>
      <DBTTable
        data={dbtRuleList}
        totalPages={totalPages}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        loading={loading}
        handleEditClick={handleEditClick}
        handleViewClick={handleViewClick}
        totalCount={totalCount}
        pageSize={pageSize}
        handleCheckboxChange={handleCheckboxChange}
      />
    </div>
  );
}
